<template>
  <tbody>
    <tr>
      <td>{{ asset.name }}</td>
      <td><span class="badge badge-pill" :class="`badge-${asset.color}`">{{ label(asset.state_label, asset.is_with, asset.organisation_id) }}</span></td>
      <td class="text-right">{{ asset.updated_at | localDateTime }}</td>
      <td class="text-right text-nowrap">

        <div v-if="asset.state !== 'executed'">
          <InertiaLink v-if="isWithMe()" class="text-nowrap btn btn-primary btn-sm" :href="route('asset.configure', [assetType.slug, asset.id])">
            Edit
          </InertiaLink>
          <div v-else>
            <button type="button" class="text-nowrap btn btn-primary btn-sm" data-toggle="modal" :data-target="`#${modalId}`">
              Resend
            </button>
            <div class="modal fade" :id="modalId" tabindex="-1" role="dialog" :aria-labelledby="`${modalId}Label`" aria-hidden="true">
              <div class="modal-dialog modal-md" role="document">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title" :id="`${modalId}Label`">Resend Contract</h5>
                    <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                  <div class="modal-body">
                    <div class="text-left form-group mb-3">
                      <div v-if="counterParty.users">
                        <Typeahead
                          class="form-group"
                          v-model="counterPartyUser"
                          label="Who would you like to resend the contract to?"
                          :items="counterParty.users"
                          placeholder="Start typing to search..."
                          :tolerance="0"
                          id-field="id"
                          :search-field="['fullname', 'email']"
                          display-field="fullname"
                          :errors="errors.reviewer_email"
                          @selected="counterPartyUserChosen"
                        >
                          <template v-slot:item="slotProps">
                            <span v-if="slotProps.match.fullname">
                              {{ slotProps.match.fullname }}
                              <br />
                            </span>
                            <small v-if="slotProps.match.email">Email: {{ slotProps.match.email }}</small>
                          </template>
                          <template v-slot:noResults>
                            <small>
                              We couldn't find a user with that name or email.
                            </small>
                          </template>
                        </Typeahead>
                      </div>
                      <div v-else>
                        <TextInput
                          v-model="reviewerEmail"
                          :errors="errors.reviewer_email"
                          type="email"
                          label="Resend To Email Address"
                          autocapitalize="off"
                          name="reviewer_email"
                          validateSuccess
                          validateError
                        />
                      </div>
                    </div>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-dismiss="modal" aria-label="Close">
                      Close
                    </button>
                    <LoadingButton
                      @click.native="resend"
                      :loading="resending"
                      type="button"
                      class="btn btn-primary btn-sm"
                    >Resend</LoadingButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="asset.state === 'executed'">
          <InertiaLink class="text-nowrap btn btn-primary btn-sm" :href="route('asset.configure', [assetType.slug, asset.id])">
            View
          </InertiaLink>
          <DownloadPDF @error="(err) => errors = err" @triggered="triggered = true" :asset="asset" />
        </div>

      </td>
    </tr>
    <tr v-if="resent || triggered || error">
      <td colspan="4" class="text-right pt-0 border-0">
        <small v-if="resent" class="text-success">We've successfully resent the email to the Counterparty.</small>
        <small v-if="triggered" class="text-success">You'll be sent an email when the PDF is ready to download.</small>
        <small v-if="error" class="text-danger">{{ this.errors[0] }}</small>
      </td>
    </tr>
    <tr v-if="asset.assets && asset.assets.length > 0">
      <td colspan="4" class="pr-0">
        <h6>&#8627; Change Requests</h6>
        <table class="w-100 table table-hover mb-0">
          <CounterPartyAsset
            v-for="changeRequest in asset.assets" :key="changeRequest.id"
            :assetType="assetType"
            :asset="changeRequest"
            :organisation="organisation"
            :counterParty="counterParty"
          />
        </table>
      </td>
    </tr>
  </tbody>
</template>

<script>
import DownloadPDF from '@/Shared/DownloadPDF';
import LoadingButton from '@/Shared/LoadingButton';

import TextInput from '@/Shared/Inputs/Text';

import Typeahead from '@/Shared/Inputs/NewTypeahead';

export default {

  name: 'CounterPartyAsset',

  components: {
    DownloadPDF,
    LoadingButton,
    TextInput,
    Typeahead,
  },

  props: {
    assetType: Object,
    asset: Object,
    organisation: Object,
    counterParty: Object,
  },

  data() {
    return {
      triggered: false,
      downloading: false,
      error: false,
      errors: {
        reviewer_email: []
      },

      modalId: `resendModal${this.asset.id}`,
      resending: false,
      resent: false,

      counterPartyUser: this.counterParty.users && this.asset && this.asset.reviewer ?
        this.counterParty.users.find((user) => String(user.email) === String(this.asset.reviewer.reviewer_email)) :
        null,

      reviewerEmail: this.asset && this.asset.reviewer ? this.asset.reviewer.reviewer_email : '',
    };
  },

  methods: {
    isWithMe() {
      const myOrganisationId = this.$page.props.auth.user.organisation.id;

      if (this.asset.is_with === 'originator' && this.asset.organisation_id === myOrganisationId) return true;
      if (this.asset.is_with === 'counter' && this.asset.counter_party_id === myOrganisationId) return true;

      return false;
    },

    label(state, isWith, assetOriginatorId) {
      if (isWith === 'originator' && assetOriginatorId === this.organisation.id) return state.organisation;
      return state.counterparty;
    },

    counterPartyUserChosen(user) {
      this.errors = [];

      if (user) this.reviewerEmail = user.email
      else this.reviewerEmail = null
    },

    resend() {
      this.resending = true
      this.resent = false

      window.axios.put(this.route('asset.resend', this.asset.id), {reviewer_email: this.reviewerEmail})
        .then(() => {
          this.resending = false;
          this.resent = true;
          $(`#${this.modalId}`).modal('hide');
        })
        .catch((error) => {
          this.resending = false;
          this.errors = error.response.data.errors;
          throw new Error(error);
        });
    },
  },
};
</script>
